import { Button, Grid2, IconButton, Typography } from "@mui/material";
import classNames from "classnames";
import { useForm, useWatch } from "react-hook-form";
import { FaChevronLeft } from "react-icons/fa6";
import { useRef, useState } from "react";
import { readUpdatedFlight } from "../flightEditState";

import { TFlightLog } from "../../../../../@types";
import { apis, callApi } from "../../../../../api";
import { validators } from "../../../../../utils";
import { CommonInput } from "../../../../common";

export type TEditFlightDetailProps = {
	cancelEdit: () => void;
	flightLog: TFlightLog;
	updateFlightLogData: (id: number, flightLog: TFlightLog) => void;
};

export type TEditFlightDetailFieldValues = {
	description: string;
	landing: string;
};

export function EditFlightDetail(props: TEditFlightDetailProps) {
	const saving = useRef(false);
	const [saveError, setSaveError] = useState("");
	const { control, handleSubmit, formState } =
		useForm<TEditFlightDetailFieldValues>({
			defaultValues: {
				description: props.flightLog.description ?? "",
				landing: props.flightLog.landing ?? "",
			},
			mode: "all",
		});

	const { description, landing } = useWatch({ control });

	const onClickEditFlightDetail = async (
		data: TEditFlightDetailFieldValues,
	) => {
		if (saving.current) return;
		saving.current = true;
		setSaveError("");
		try {
			const body: Partial<{
				description?: string;
				landing?: string;
				shared?: boolean;
			}> = {};
			if (data.description !== (props.flightLog.description ?? "")) {
				body.description = data.description.trim();
			}

			if (data.landing !== (props.flightLog.landing ?? "")) {
				body.landing = data.landing;
			}

			const response = await callApi({
				method: "PATCH",
				url: `${apis.FLIGHT_LOGS}/${props.flightLog.id}`,
				body,
			});

			const updated = readUpdatedFlight(response, props.flightLog.id ?? 0);
			props.updateFlightLogData(updated.id!, updated);
			props.cancelEdit();
		} catch (error) {
			setSaveError(
				"Unable to save changes. Your edits are preserved; please try again.",
			);
		} finally {
			saving.current = false;
		}
	};

	const buttonDisabled =
		formState.isSubmitting ||
		(description?.trim() === (props.flightLog.description ?? "") &&
			landing?.trim() === (props.flightLog.landing ?? "")) ||
		Object.keys(formState.errors).length > 0;

	return (
		<Grid2
			size={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
			className="flight-log-detail">
			<div className="flight-log-detail-header">
				<IconButton onClick={props.cancelEdit}>
					<FaChevronLeft className="normal-icon" />
				</IconButton>
				<Typography className="normal-text">Edit Flight Takeaways</Typography>
			</div>
			<form
				className="flight-log-detail-content"
				onSubmit={handleSubmit(onClickEditFlightDetail)}>
				<Typography className="normal-text">Takeaways</Typography>
				<CommonInput
					control={control}
					name="description"
					placeholder="Enter Takeaways"
					multiline
				/>

				<Typography marginTop="16px" className="normal-text">
					Landings
				</Typography>
				<CommonInput
					control={control}
					name="landing"
					placeholder="Enter Landings"
					rules={{
						pattern: {
							value: validators.integer,
							message: "Please enter an integer",
						},
					}}
				/>

				<div className="save-button-container">
					{saveError && (
						<Typography role="alert" color="error">
							{saveError}
						</Typography>
					)}
					<Button
						type="submit"
						disabled={buttonDisabled}
						className={classNames("normal-button save-button", {
							"primary-button": !buttonDisabled,
							"disabled-button": buttonDisabled,
						})}>
						Save
					</Button>
				</div>
			</form>
		</Grid2>
	);
}
